import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UploadSimple, Trash, DownloadSimple } from "@phosphor-icons/react";

const PLATFORMS = ["Spotify", "Apple Music", "TIDAL", "Amazon Music", "YouTube Music", "Deezer", "Pandora", "SoundCloud", "Audiomack", "TikTok", "Other"];
const inputCls = "w-full bg-transparent border border-zinc-800 px-3 py-2 text-sm focus:border-red-600 focus:outline-none";
const TEMPLATE = "release,platform,period,streams,saves,royalty_usd\n<release id, ISRC or exact title>,Spotify,2026-05,12400,310,42.18\n";

export default function AdminReports({ releases }) {
  const [reports, setReports] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ release_id: "", platform: "Spotify", period: new Date().toISOString().slice(0, 7), streams: "", saves: "", royalty_usd: "" });

  const load = () => api.get("/admin/reports").then(({ data }) => setReports(data.reports));
  useEffect(() => { load(); }, []);

  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reports/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.inserted} rows${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const addManual = async (e) => {
    e.preventDefault();
    if (!form.release_id) { toast.error("Pick a release"); return; }
    setBusy(true);
    try {
      await api.post("/admin/reports", { ...form, streams: Number(form.streams || 0), saves: Number(form.saves || 0), royalty_usd: Number(form.royalty_usd || 0) });
      toast.success("Report added");
      setForm({ ...form, streams: "", saves: "", royalty_usd: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await api.delete(`/admin/reports/${id}`);
    toast.success("Deleted"); load();
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "asylum_dsp_report_template.csv"; a.click();
  };

  return (
    <div data-testid="admin-reports">
      <div className="grid lg:grid-cols-2 border border-zinc-800 mb-8">
        <div className="p-6 border-r border-zinc-800">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Import DSP Report</div>
          <p className="text-xs text-zinc-500 mb-4">Upload the monthly usage/royalty CSV from your distributor or DSP. Columns: <span className="font-mono text-zinc-300">release, platform, period, streams, saves, royalty_usd</span>. Release can be ID, ISRC, or exact title.</p>
          <div className="flex gap-2">
            <label className={`flex-1 border border-dashed border-zinc-700 hover:border-red-600 p-5 text-center cursor-pointer transition-colors ${busy ? "opacity-50" : ""}`}>
              <UploadSimple size={20} className="mx-auto mb-2 text-red-500" />
              <div className="text-xs">{busy ? "Importing..." : "Click to upload CSV"}</div>
              <input type="file" accept=".csv,text/csv" className="hidden" data-testid="report-csv-input" onChange={(e) => importCsv(e.target.files?.[0])} />
            </label>
            <button onClick={downloadTemplate} data-testid="report-template-btn" className="border border-zinc-700 px-4 text-[10px] tracking-[0.2em] uppercase hover:border-white inline-flex flex-col items-center justify-center gap-1">
              <DownloadSimple size={16} /> Template
            </button>
          </div>
          {importResult && (
            <div className="mt-4 text-xs border border-zinc-800 p-3" data-testid="import-result">
              <span className="text-green-500">{importResult.inserted} inserted</span> · <span className="text-yellow-500">{importResult.skipped} skipped</span>
              {importResult.errors.map((e, i) => <div key={i} className="text-zinc-500 mt-1">{e}</div>)}
            </div>
          )}
        </div>
        <form onSubmit={addManual} className="p-6 space-y-3">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Manual Entry</div>
          <select value={form.release_id} onChange={(e) => setForm({ ...form, release_id: e.target.value })} data-testid="report-release" className={inputCls}>
            <option value="" className="bg-zinc-900">Select release…</option>
            {releases.map(r => <option key={r.id} value={r.id} className="bg-zinc-900">{r.title} — {r.artist_name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} data-testid="report-platform" className={inputCls}>
              {PLATFORMS.map(p => <option key={p} className="bg-zinc-900">{p}</option>)}
            </select>
            <input type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="report-period" className={inputCls} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input type="number" min="0" placeholder="Streams" value={form.streams} onChange={(e) => setForm({ ...form, streams: e.target.value })} data-testid="report-streams" className={inputCls} />
            <input type="number" min="0" placeholder="Saves" value={form.saves} onChange={(e) => setForm({ ...form, saves: e.target.value })} data-testid="report-saves" className={inputCls} />
            <input type="number" min="0" step="0.01" placeholder="Royalty $" value={form.royalty_usd} onChange={(e) => setForm({ ...form, royalty_usd: e.target.value })} data-testid="report-royalty" className={inputCls} />
          </div>
          <button disabled={busy} data-testid="report-submit" className="w-full bg-red-600 text-white py-3 text-[10px] tracking-[0.2em] uppercase btn-brutal btn-brutal-red disabled:opacity-50">Post Report</button>
        </form>
      </div>

      <div className="border border-zinc-800 overflow-x-auto" data-testid="admin-reports-table">
        <div className="min-w-[800px]">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-800 text-[10px] tracking-[0.2em] uppercase text-zinc-500 bg-zinc-950">
            <div className="col-span-4">Release</div><div className="col-span-2">Platform</div><div className="col-span-1">Period</div>
            <div className="col-span-1 text-right">Streams</div><div className="col-span-1 text-right">Saves</div><div className="col-span-2 text-right">Royalty</div><div className="col-span-1" />
          </div>
          {reports.length === 0 ? <div className="p-10 text-center text-zinc-500 text-sm">No reports posted yet.</div> : reports.map(r => (
            <div key={r.id} className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-800 last:border-b-0 row-hover items-center text-sm" data-testid={`report-row-${r.id}`}>
              <div className="col-span-4 truncate"><div>{r.release_title || r.release_id}</div><div className="text-[10px] text-zinc-500">{r.artist_name} · {r.source}</div></div>
              <div className="col-span-2 text-zinc-400">{r.platform}</div>
              <div className="col-span-1 font-mono text-xs text-zinc-500">{r.period}</div>
              <div className="col-span-1 text-right font-mono text-xs">{Number(r.streams).toLocaleString()}</div>
              <div className="col-span-1 text-right font-mono text-xs">{Number(r.saves).toLocaleString()}</div>
              <div className="col-span-2 text-right font-mono text-xs">${Number(r.royalty_usd).toFixed(2)}</div>
              <div className="col-span-1 text-right"><button onClick={() => del(r.id)} data-testid={`report-delete-${r.id}`} className="text-zinc-600 hover:text-red-500"><Trash size={14} /></button></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
